import { useState } from 'react'
import { useTranslation } from '../i18n'
import { useApi, apiPut, apiDelete } from '../hooks/useApi'
import type { Test, Package } from '../types'

export function TestMaintenanceSection() {
  const { t } = useTranslation()
  const [packageFilter, setPackageFilter] = useState('')
  const [editingTest, setEditingTest] = useState<string | null>(null)
  const [budgetHours, setBudgetHours] = useState('')
  const [gapPercentage, setGapPercentage] = useState('')
  const [saving, setSaving] = useState(false)

  const packages = useApi<Package[]>('/tests/packages')
  const allTests = useApi<Test[]>('/tests')

  const filteredTests = packageFilter
    ? (allTests.data ?? []).filter((t) => t.className.startsWith(packageFilter))
    : (allTests.data ?? [])

  const handleEditBudget = (test: Test) => {
    setEditingTest(test.id)
    setBudgetHours(test.budgetHours?.toString() ?? '')
    setGapPercentage(test.gapPercentage.toString())
  }

  const handleSaveBudget = async (test: Test) => {
    const hours = parseFloat(budgetHours)
    const gap = parseFloat(gapPercentage) || 0

    if (isNaN(hours)) {
      alert(t('error.invalidHours'))
      return
    }

    setSaving(true)
    try {
      await apiPut(
        `/tests/${encodeURIComponent(test.className)}/${encodeURIComponent(test.methodName)}/budget`,
        {
          hours,
          gapPercentage: gap,
        }
      )
      setEditingTest(null)
      allTests.refetch()
    } catch (err) {
      alert(err instanceof Error ? err.message : t('error.failedToSaveBudget'))
    } finally {
      setSaving(false)
    }
  }

  const handleClearBudget = async (test: Test) => {
    if (!confirm(t('confirm.clearBudget'))) return

    setSaving(true)
    try {
      await apiDelete(
        `/tests/${encodeURIComponent(test.className)}/${encodeURIComponent(test.methodName)}/budget`
      )
      allTests.refetch()
    } catch (err) {
      alert(err instanceof Error ? err.message : t('error.failedToClearBudget'))
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingTest(null)
    setBudgetHours('')
    setGapPercentage('')
  }

  return (
    <div data-testid="test-maintenance-section" className="card" style={{ marginTop: '1.5rem' }}>
      <h2 className="card-title">{t('component.testEffortMaintenance')}</h2>
      <p style={{ color: 'var(--color-gray-400)', marginBottom: '1rem' }}>
        {t('misc.testEffortDesc')}
      </p>

      <div style={{ marginBottom: '1rem' }}>
        <label
          htmlFor="maintenance-package-filter"
          style={{ display: 'block', marginBottom: '0.5rem' }}
        >
          {t('label.filterByPackage')}
        </label>
        <select
          id="maintenance-package-filter"
          value={packageFilter}
          onChange={(e) => setPackageFilter(e.target.value)}
          data-testid="maintenance-package-filter"
          className="input"
          style={{ maxWidth: '400px' }}
        >
          <option value="">{t('label.allPackages')}</option>
          {packages.data?.map((pkg) => (
            <option key={pkg.name} value={pkg.name}>
              {pkg.name} ({pkg.testCount})
            </option>
          ))}
        </select>
      </div>

      {allTests.loading ? (
        <div style={{ color: 'var(--color-gray-400)', textAlign: 'center', padding: '2rem' }}>
          {t('label.loading')}
        </div>
      ) : filteredTests.length === 0 ? (
        <div style={{ color: 'var(--color-gray-400)', textAlign: 'center', padding: '2rem' }}>
          {t('empty.noTests')}
        </div>
      ) : (
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>{t('label.test')}</th>
                <th>{t('label.status')}</th>
                <th>{t('label.hours')}</th>
                <th>{t('label.gap')}</th>
                <th>{t('label.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredTests.map((test) => (
                <tr key={test.id}>
                  <td>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      <div>{test.className}</div>
                      <div style={{ color: 'var(--color-gray-400)' }}>.{test.methodName}</div>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        test.status === 'PASSED'
                          ? 'status-passed'
                          : test.status === 'FAILED'
                            ? 'status-failed'
                            : test.status === 'SKIPPED'
                              ? 'status-skipped'
                              : ''
                      }`}
                    >
                      {test.status ?? t('status.unknown')}
                    </span>
                  </td>
                  {editingTest === test.id ? (
                    <>
                      <td>
                        <input
                          type="number"
                          value={budgetHours}
                          onChange={(e) => setBudgetHours(e.target.value)}
                          className="input input-sm"
                          style={{ width: '80px' }}
                          placeholder={t('label.hours')}
                          data-testid="edit-hours-input"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={gapPercentage}
                          onChange={(e) => setGapPercentage(e.target.value)}
                          className="input input-sm"
                          style={{ width: '60px' }}
                          placeholder="%"
                          data-testid="edit-gap-input"
                        />
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleSaveBudget(test)}
                            disabled={saving}
                            aria-label={t('button.save')}
                            title={t('button.save')}
                            data-testid="save-budget-button"
                          >
                            <i className="pi pi-check" aria-hidden="true" />
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleCancelEdit}
                            disabled={saving}
                            aria-label={t('button.cancel')}
                            title={t('button.cancel')}
                            data-testid="cancel-edit-button"
                          >
                            <i className="pi pi-times" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{test.budgetHours?.toFixed(1) ?? '-'}</td>
                      <td>{test.gapPercentage}%</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleEditBudget(test)}
                            aria-label="Edit"
                            title="Edit"
                            data-testid="edit-budget-button"
                          >
                            <i className="pi pi-pencil" aria-hidden="true" />
                          </button>
                          {test.budgetHours !== null && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleClearBudget(test)}
                              disabled={saving}
                              aria-label={t('button.clear')}
                              title={t('button.clear')}
                              data-testid="clear-budget-button"
                            >
                              <i className="pi pi-eraser" aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
